// App-wide VersionSkewMonitor singleton wired to real browser APIs.
//
// Layering (eslint boundaries: lib may only import lib): this file knows
// NOTHING about robot/trade state. The shell (app.tsx) injects the trading
// guard via configureVersionSkewReloadGuard; until then the guard defaults to
// "blocked" — fail-safe for a trading app, a reload is never fired under an
// unknown robot state. useNetworkStatus feeds builds via reportLivenessBuild.

import {
  VersionSkewMonitor,
  type VersionSkewDeps,
} from "./version-skew-monitor";

type ReloadGuard = () => boolean;

const FAIL_SAFE_GUARD: ReloadGuard = () => true;

let reloadGuard: ReloadGuard = FAIL_SAFE_GUARD;
let reloadSeam: () => void = () => window.location.reload();
let appMonitor: VersionSkewMonitor | null = null;

/** Shell injects "robot running / trade pending" here (see app.tsx). */
export function configureVersionSkewReloadGuard(guard: ReloadGuard): void {
  reloadGuard = guard;
}

/** Forward the `build` field of a liveness response to the app monitor. */
export function reportLivenessBuild(build: unknown): void {
  ensureAppMonitor().reportLivenessBuild(build);
}

/** Re-check a deferred reload (poll tick / tab became visible). */
export function evaluatePendingVersionSkewReload(): void {
  ensureAppMonitor().evaluatePendingReload();
}

/** Reactive store surface for the skew badge (useSyncExternalStore). */
export function subscribeVersionSkewPending(listener: () => void): () => void {
  return ensureAppMonitor().subscribePending(listener);
}

/** New build this tab still has NOT reloaded onto, or null when current. */
export function getPendingVersionSkewBuild(): string | null {
  return ensureAppMonitor().getPendingBuild();
}

/** Operator-initiated reload from the skew badge. Bypasses the trading guard
 *  on purpose — an explicit click is consent — but reuses the reload seam so
 *  tests observe it. */
export function reloadNowForVersionSkew(): void {
  reloadSeam();
}

/** Test seam: replace window.location.reload (jsdom can't stub location). */
export function setVersionSkewReloadSeamForTests(reload: () => void): void {
  reloadSeam = reload;
}

/** Test seam: drop the singleton + restore the fail-safe guard. */
export function resetVersionSkewForTests(): void {
  appMonitor = null;
  reloadGuard = FAIL_SAFE_GUARD;
  reloadSeam = (): void => window.location.reload();
}

function ensureAppMonitor(): VersionSkewMonitor {
  appMonitor ??= new VersionSkewMonitor(browserVersionSkewDeps());
  return appMonitor;
}

function browserVersionSkewDeps(): VersionSkewDeps {
  return {
    isReloadBlocked: (): boolean => reloadGuard(),
    isTabVisible: (): boolean => document.visibilityState === "visible",
    reloadPage: (): void => reloadSeam(),
    sessionStore: browserSessionStore(),
  };
}

function browserSessionStore(): Pick<Storage, "getItem" | "setItem"> {
  // sessionStorage ACCESS itself throws in some privacy modes — the monitor
  // already try/catches getItem/setItem, this guards the property read.
  try {
    return globalThis.sessionStorage;
  } catch {
    return inMemorySessionStoreFallback();
  }
}

function inMemorySessionStoreFallback(): Pick<Storage, "getItem" | "setItem"> {
  const entries = new Map<string, string>();
  return {
    getItem: (key: string): string | null => entries.get(key) ?? null,
    setItem: (key: string, value: string): void => {
      entries.set(key, value);
    },
  };
}
